const isState = localState =>
  (typeof localState === 'object' && localState !== null) ||
  typeof localState === 'function';

const assertState = localState => {
  if (!isState(localState)) throw new TypeError('localState is required');
};

export const createAbortableRequestTracker = () => {
  const requestsByState = new WeakMap();

  const requestsFor = localState => {
    assertState(localState);
    let requests = requestsByState.get(localState);
    if (!requests) {
      requests = new Map();
      requestsByState.set(localState, requests);
    }
    return requests;
  };

  const begin = (localState, group = 'default') => {
    if (typeof group !== 'string' || !group.trim()) {
      throw new TypeError('request group is required');
    }

    const normalizedGroup = group.trim();
    const requests = requestsFor(localState);
    requests.get(normalizedGroup)?.controller.abort();

    const controller = new AbortController();
    const request = Object.freeze({
      controller,
      group: normalizedGroup,
      signal: controller.signal,
    });
    requests.set(normalizedGroup, request);
    return request;
  };

  const isCurrent = (localState, request) => {
    if (!isState(localState) || !request || request.signal.aborted) {
      return false;
    }
    return requestsByState.get(localState)?.get(request.group) === request;
  };

  const finish = (localState, request) => {
    if (!isCurrent(localState, request)) return false;

    const requests = requestsByState.get(localState);
    requests.delete(request.group);
    if (requests.size === 0) requestsByState.delete(localState);
    return true;
  };

  const reset = localState => {
    if (!isState(localState)) return false;
    const requests = requestsByState.get(localState);
    if (!requests) return false;
    requests.forEach(request => request.controller.abort());
    requestsByState.delete(localState);
    return true;
  };

  return { begin, finish, isCurrent, reset };
};

export default createAbortableRequestTracker;
